import first from 'lodash/first';

import {STATUS_CODES, STATUS_DESCRIPTIONS} from '~/types/statuses';
import {
  IsDamageRequest,
  IsReplacementVehicle,
  REQUEST_TYPES,
} from '~/types/request';
import {
  isLessorStatus,
  isRobTaskApproved,
  isRobTaskDenied,
  ROB_TASK_STATUS,
  ROB_TASK_STATUS_LABELS,
  TIRE_REPAIR,
} from '~/types/rob';
import {
  ROB_REPLACEMENT_STAGE_ORDER,
  ROB_REPLACEMENT_STAGE_TITLE,
} from '~/types/replacement';

export const isReadOnlyStatus = (status) =>
  status === STATUS_CODES.Cancelled ||
  status === STATUS_CODES.Done ||
  status === STATUS_CODES.DoneNoInvoice ||
  status === STATUS_CODES.Invoiced ||
  status === STATUS_CODES.Archived;

export const shouldRedirectToCalendar = (request) =>
  !request ||
  IsReplacementVehicle(request.request_type) ||
  IsDamageRequest(request.request_type);

export const deriveRequestVehicleFlags = (request_type) => ({
  is_fleet_car: request_type === REQUEST_TYPES.FLEET_REQUEST,
  is_retail_car: request_type === REQUEST_TYPES.SERVICE_REQUEST,
});

export const resolveRequestRobStatus = (request, is_fleet_car, is_retail_car) =>
  is_fleet_car || is_retail_car ? request?.status : request?.rob?.status;

export const isRequestEditable = (request, is_fleet_car, is_retail_car) =>
  !isLessorStatus(
    resolveRequestRobStatus(request, is_fleet_car, is_retail_car),
  ) && !isReadOnlyStatus(request?.status);

export const shouldFetchRequestInvoice = (
  status,
  is_fleet_car,
  is_retail_car,
  rob_invoicing_status,
) =>
  (status === STATUS_CODES.Done ||
    status === STATUS_CODES.DoneNoInvoice ||
    status === STATUS_CODES.Invoiced ||
    status === STATUS_CODES.Archived) &&
  (is_fleet_car ||
    is_retail_car ||
    rob_invoicing_status === 'ReadyForInvoicing' ||
    rob_invoicing_status === 'Invoiced');

export const shouldRefreshRequestDetails = (lastFetchDate, updateDate) =>
  updateDate.isAfter(lastFetchDate, 'seconds');

export const hasReplacementVehicleRequest = (replacement_vehicle) =>
  !!(replacement_vehicle?.fleet_request ?? replacement_vehicle?.rob);

export const getLatestReplacementStageKey = (stages) =>
  first(
    Object.keys(stages ?? {}).sort(
      (a, b) =>
        ROB_REPLACEMENT_STAGE_ORDER.indexOf(b) -
        ROB_REPLACEMENT_STAGE_ORDER.indexOf(a),
    ),
  );

export const resolveReplacementVehicleSubtitle = (replacement_vehicle) => {
  const status = replacement_vehicle?.replacement?.status;
  if (
    status === STATUS_CODES.Done ||
    status === STATUS_CODES.DoneNoInvoice ||
    status === STATUS_CODES.Invoiced ||
    status === STATUS_CODES.Archived
  ) {
    return STATUS_DESCRIPTIONS[status];
  }

  const request =
    replacement_vehicle?.fleet_request ?? replacement_vehicle?.rob;
  if (request?.task_status === ROB_TASK_STATUS.CancellationApproved) {
    return ROB_TASK_STATUS_LABELS[ROB_TASK_STATUS.CancellationApproved];
  }

  if (request?.stages) {
    const rob_stage = getLatestReplacementStageKey(request.stages);
    if (
      isLessorStatus(
        replacement_vehicle?.fleet_request?.status ??
          replacement_vehicle?.rob?.rob_status,
      )
    ) {
      return `Voorstel ${ROB_REPLACEMENT_STAGE_TITLE[rob_stage].toLowerCase()}`;
    }

    const stage_status = request.stages[rob_stage].status;
    let stageStatusLabel = '';
    if (isRobTaskDenied(stage_status)) {
      stageStatusLabel = 'niet akkoord';
    } else if (isRobTaskApproved(stage_status)) {
      const prefix = rob_stage === 'checkin' ? '' : 'voorlopig ';
      stageStatusLabel = `${prefix}akkoord`;
    }

    return `${ROB_REPLACEMENT_STAGE_TITLE[rob_stage]} ${stageStatusLabel}`.trim();
  }

  return '';
};

export const mapTirePartActions = (part) => {
  switch (part.action) {
    case 'Current_TireRepair':
      return part.repairs.map((item) =>
        item === TIRE_REPAIR.Wielomwisselen ? 'Omwisselen' : item,
      );
    case 'Current_Replace':
      return ['Bandvervanging'];
    case 'Current_Store':
    case 'Current_Remove':
      return ['Wissel zonder velg'];
    case 'Current_StoreWheel':
      return ['Wissel met velg'];
    default:
      break;
  }
  return [];
};

export const getUniqueTireActions = (tasks) => [
  ...new Set(
    tasks
      .flatMap((task) => task.parts ?? [])
      .flatMap((part) => mapTirePartActions(part)),
  ),
];

export const resolveTaskSubtitleData = ({
  lessor,
  tasks,
  is_maintenance_item,
  is_tires_item,
  is_sla_item,
  ready,
  rob_components,
  is_fleet_car,
  is_retail_car,
}) => {
  if (!ready) {
    return {text: 'Keuze maken'};
  }

  if (is_tires_item) {
    const tire_actions = getUniqueTireActions(tasks);
    return {text: `SLA · ${tire_actions.join(', ')}`};
  }

  if (is_sla_item) {
    const is_bk_task = tasks.some((item) =>
      ['SLA_TASK_DURATION', 'SLA_TASK_AMOUNT'].includes(item.type),
    );

    if (is_bk_task) {
      const main_task = tasks[0];
      const text =
        main_task.type === 'SLA_TASK_DURATION' &&
        main_task.sla?.duration === main_task.duration
          ? 'SLA'
          : 'Invoer';
      return {text};
    }

    return {text: `SLA · ${lessor}`};
  }

  let type;
  let operations;

  if (tasks.every((item) => !item.reference)) {
    type = 'Invoer';
    operations = [
      ...new Set(
        tasks
          .map(
            (task) =>
              rob_components[task.rob_code]?.operations.find(
                (item) => item.operation.code === task.operation,
              )?.operation.description,
          )
          .filter(Boolean),
      ),
    ];
  } else {
    const main_task = tasks.find((item) => item.is_main_task);
    type = main_task?.source ?? 'TecRMI';
    if (is_maintenance_item || (!is_fleet_car && !is_retail_car)) {
      operations = tasks
        .sort((a, b) => {
          if (a.type && !b.type) {
            return -1;
          }
          if (!a.type && b.type) {
            return 1;
          }
          if (a.duration === b.duration) {
            return a.work_text.localeCompare(b.work_text);
          }
          return b.duration - a.duration;
        })
        .map((task) => task.work_text ?? 'Invoer');
    }
  }

  return {type, operations};
};
